/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-29 13:48:42
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input, message, Tag } from 'antd'
import {
  calcCharactersLen,
  debounce,
  filterIllegalCharacters,
  raplaceEmoji as replaceEmoji,
  sliceCharactersLen
} from '../../../utils/utils'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  selectLocationRecommend,
  setLocationRecommend
} from '../../../slices/photosByLocationSlice'

const debouncedOverInputMessage = debounce(
  () => {
    message.warning('最多可输入16个汉字，32个字符!')
  },
  1000,
  true
)

const debouncedOverKeywordsLengthMessage = debounce(
  () => {
    message.warning('最多支持3个关键词!')
  },
  1000,
  true
)

interface TagsInputProps {
  tags: string[]
  inputValue: string
  onChange: (value: string[]) => void
  onInputChange: (keyword: string) => void
  onSensorOfClickEvent: (clickItem: string, restProperties: any) => void
  onFocusChange?: (isFocused: boolean) => void // 新增：聚焦状态变化回调
}

const defaultInputWidth = 180

const TagsInput: React.FC<TagsInputProps> = ({
  tags = [],
  inputValue,
  onChange = () => {},
  onInputChange = () => {},
  onSensorOfClickEvent = () => {},
  onFocusChange = () => {} // 新增：聚焦状态变化回调
}) => {
  const hiddenValueRef: any = useRef(null)
  const [inputWidth, setInputWidth] = useState(defaultInputWidth)
  const locationRecommend = useAppSelector(selectLocationRecommend)
  const dispatch = useAppDispatch()

  useEffect(() => {
    setInputWidth(defaultInputWidth)
  }, [tags])

  //刚开始输入第一个关键词时，显示提示
  const showInputTips = useMemo(() => {
    return inputValue && tags.length === 0
  }, [inputValue, tags])

  const inputPlaceholder = useMemo(() => {
    if (tags.length === 0) {
      return '水印内容、姓名、地点…'
    } else if (tags.length <= 2) {
      return '加搜索词'
    }
    return ''
  }, [tags])

  const handleTagRemove = useCallback(
    (index) => {
      //如果删除的tag，和地址关键词相同，清空唯一的地址关键词
      if (locationRecommend && locationRecommend.word === tags[index]) {
        dispatch(setLocationRecommend(null))
      }
      const newValue = tags.filter((item, i) => i !== index)
      onChange(newValue)
    },
    [tags, onChange, locationRecommend]
  )

  const handleSubmitTag = useCallback(() => {
    if (inputValue) {
      onChange(Array.from(new Set([...tags, inputValue])))
      onInputChange('')
      setInputWidth(defaultInputWidth)
    }
  }, [inputValue, onInputChange, onChange, tags])

  const handleInputChange = useCallback(
    (e) => {
      if (tags.length >= 3) {
        debouncedOverKeywordsLengthMessage()
        return
      }
      //过滤特殊字符
      const textValue = e.target.value
      let filtedValue = filterIllegalCharacters(textValue, ['/'])
      filtedValue = replaceEmoji(filtedValue)
      if (filtedValue.length < textValue.length) {
        message.warning('特殊字符已过滤!')
      }

      //清空输入框时，重置宽度
      if (filtedValue.trim() === '') {
        setInputWidth(defaultInputWidth)
        onInputChange('')
        return
      }

      //限制输入内容长度
      if (!calcCharactersLen(filtedValue, 32)) {
        //输入空格时，提交
        if (filtedValue.slice(-1) === ' ') {
          handleSubmitTag()
          return
        }
        debouncedOverInputMessage()
        onInputChange(sliceCharactersLen(filtedValue, 32))
      } else {
        //输入空格时，提交
        if (filtedValue.slice(-1) === ' ') {
          handleSubmitTag()
          return
        }
        onInputChange(filtedValue)
      }
    },
    [handleSubmitTag, onInputChange, tags]
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (inputValue === '' && e.key === 'Backspace') {
        handleTagRemove(tags.length - 1)
        e.preventDefault()
      }
    },
    [inputValue, handleTagRemove, tags, onInputChange]
  )

  const handleInputFocus = useCallback(() => {
    onSensorOfClickEvent('searchContent', {})
    onFocusChange(true) // 新增：通知父组件输入框已聚焦
  }, [onSensorOfClickEvent, onFocusChange])

  const handleInputBlur = useCallback(() => {
    handleSubmitTag()
    onFocusChange(false) // 新增：通知父组件输入框已失焦
  }, [handleSubmitTag, onFocusChange])

  useEffect(() => {
    if (hiddenValueRef.current && inputValue) {
      setInputWidth(hiddenValueRef.current.offsetWidth + 4)
    }
  }, [inputValue])

  return (
    <div className="tags-input-wrapper">
      {tags.map((item, index) => {
        return (
          <Tag key={index} closable onClose={() => handleTagRemove(index)}>
            {item}
          </Tag>
        )
      })}
      <Input
        style={{ width: inputWidth }}
        placeholder={inputPlaceholder}
        value={inputValue}
        onChange={handleInputChange}
        onPressEnter={handleSubmitTag}
        onBlur={handleInputBlur} // 修改：使用新的失焦处理函数
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
      />
      <span
        style={{ visibility: 'hidden' }}
        className="hidden-value"
        ref={hiddenValueRef}
      >
        {inputValue}
      </span>
      {showInputTips && (
        <span className="input-tips">按空格可加多个搜索词</span>
      )}
    </div>
  )
}

export default TagsInput
